import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getErrorMessage } from '../../api/apiClient';
import { getProjects } from '../../api/projectsApi';
import type { ProjectListItem } from '../../types/project';
import './Dashboard.css';

function formatStatus(status: string | null | undefined): string {
  return String(status || 'Unknown').replaceAll('_', ' ');
}

function ProjectList() {
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    getProjects()
      .then((response) => {
        if (active) {
          setProjects(response.items || []);
        }
      })
      .catch((requestError: unknown) => {
        if (active) {
          setError(getErrorMessage(requestError, 'Projects could not be loaded.'));
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return <div className="dashboard-route-status" role="status">Loading projects...</div>;
  }

  if (error) {
    return (
      <section className="dashboard-page">
        <div className="dashboard-empty-state" role="alert">
          <h1>Projects unavailable</h1>
          <p>{error}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="dashboard-page">
      <div className="dashboard-page-header">
        <div>
          <p className="eyebrow">Portfolio</p>
          <h1>Projects</h1>
        </div>
      </div>

      <div className="project-table" role="table" aria-label="Projects">
        <div className="project-table-row project-table-head" role="row">
          <span>Project</span>
          <span>Status</span>
          <span>Health</span>
          <span>Target</span>
        </div>
        {projects.map((project) => (
          <Link key={project.id} className="project-table-row" role="row" to={`/dashboard/projects/${project.id}`}>
            <span>
              <strong>{project.name}</strong>
              <small>{project.clientOrganization}</small>
            </span>
            <span>{formatStatus(project.status)}</span>
            <span className={`status-pill status-${String(project.health || 'unknown').toLowerCase().replaceAll('_', '-')}`}>
              {formatStatus(project.health)}
            </span>
            <span>{project.targetDate}</span>
          </Link>
        ))}
        {projects.length === 0 && (
          <p className="inline-empty-state project-table-empty">No projects are assigned to this account.</p>
        )}
      </div>
    </section>
  );
}

export default ProjectList;
